import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import type { PlayerEnrollmentPublic, SportPublic, AgeGroupPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { LoadErrorCard } from "@/components/load-error-card";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Trash2 } from "lucide-react";
import { CoachNotesSection } from "@/components/coach-notes-section";
import { PlayerHealthSection } from "@/components/player-health-section";
import { PlayerInjurySection } from "@/components/player-injury-section";

// 158 -- permitted-to-play colour-coding for an age-group override.
const OVERRIDE_PERMISSION_BADGE: Record<
  PlayerEnrollmentPublic["override_permission"],
  { cls: string; label: string }
> = {
  permitted: { cls: "bg-[#E1F5EE] text-[#085041]", label: "Permitted" },
  conditional: { cls: "bg-[#FAEEDA] text-[#633806]", label: "Conditional - indemnity outstanding" },
  not_permitted: { cls: "bg-[#FCEBEB] text-[#791F1F]", label: "Not permitted - exceeds +2 cap" },
};
const OVERRIDE_DIRECTION_LABEL: Record<PlayerEnrollmentPublic["override_direction"], string> = {
  default: "Default",
  playing_up: "Playing up",
  playing_down: "Playing down",
};

// Player edit form with inline enrollment management

export default function PlayerEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [firstName, setFirstName] = useState("");
  const [surname, setSurname] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [familyCode, setFamilyCode] = useState("");
  const [nickname, setNickname] = useState("");
  const [playerCode, setPlayerCode] = useState("");
  const [grade, setGrade] = useState("");
  const [active, setActive] = useState(true);
  const [enrollments, setEnrollments] = useState<PlayerEnrollmentPublic[]>([]);
  const [sports, setSports] = useState<SportPublic[]>([]);
  const [allAgeGroups, setAllAgeGroups] = useState<AgeGroupPublic[]>([]);
  const [clubs, setClubs] = useState<{ id: string; name: string }[]>([]); // 149
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 204 -- a failed initial load renders a friendly full-page state, not an empty form.
  const [loadError, setLoadError] = useState<unknown>(null);
  const [enrollmentError, setEnrollmentError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api.getPlayer(id)
      .then((playerRes) => {
        setFirstName(playerRes.player.first_name ?? "");
        setSurname(playerRes.player.surname ?? "");
        setDateOfBirth(playerRes.player.date_of_birth ?? "");
        setFamilyCode(playerRes.player.family_code ?? "");
        setNickname(playerRes.player.nickname ?? "");
        setPlayerCode(playerRes.player.player_code ?? "");
        setGrade(playerRes.player.grade !== null ? String(playerRes.player.grade) : "");
        setActive(playerRes.player.active);
        setEnrollments(playerRes.player.enrollments);
        // Load school-scoped sports + age groups in parallel
        return Promise.all([
          api.getSchoolSports(playerRes.player.school_id),
          api.getAllAgeGroups(),
          api.getSchools(),
        ]).then(([sportsRes, agRes, schoolsRes]) => {
          setSports(sportsRes.sports.map((s) => ({ id: s.sport_id, name: s.sport_name, default_court_type_id: null, court_type_name: s.court_type_name ?? null, enabled: true })));
          setAllAgeGroups(agRes.age_groups);
          // 149 -- clubs are schools-as-venues of type 'club'
          setClubs(schoolsRes.schools.filter((s) => s.venue_type === "club").map((s) => ({ id: s.id, name: s.name })));
        });
      })
      .catch((e) => setLoadError(e))
      .finally(() => setLoading(false));
  }, [id]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    if (!firstName.trim() && !surname.trim()) {
      setError("At least one of first name or surname is required");
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      const gradeNum = grade ? parseInt(grade, 10) : null;
      await api.updatePlayer(id, {
        first_name: firstName.trim(),
        surname: surname.trim(),
        nickname: nickname.trim(),
        date_of_birth: dateOfBirth,
        family_code: familyCode || null,
        player_code: playerCode.trim() || null,
        grade: gradeNum,
        active,
      });
      // 079 -- notify any open age-group pool to re-fetch without a navigation
      window.dispatchEvent(new CustomEvent("player:updated"));
      navigate("/players");
    } catch (err) {
      setError(
        err instanceof api.ApiError ? err.message : "Failed to update player",
      );
    } finally {
      setSubmitting(false);
    }
  }

  // Enrollment management

  const enrolledSportIds = enrollments.map((e) => e.sport_id);
  const availableSports = sports.filter((s) => !enrolledSportIds.includes(s.id));

  async function handleAddEnrollment(sportId: string) {
    if (!id) return;
    setEnrollmentError(null);
    try {
      const res = await api.addPlayerEnrollment(id, { sport_id: sportId });
      setEnrollments((prev) => [...prev, res.enrollment]);
    } catch (err) {
      setEnrollmentError(
        err instanceof api.ApiError ? err.message : "Failed to add enrollment",
      );
    }
  }

  async function handleRemoveEnrollment(sportId: string) {
    if (!id) return;
    setEnrollmentError(null);
    try {
      await api.deletePlayerEnrollment(id, sportId);
      setEnrollments((prev) => prev.filter((e) => e.sport_id !== sportId));
    } catch (err) {
      setEnrollmentError(
        err instanceof api.ApiError ? err.message : "Failed to remove enrollment",
      );
    }
  }

  async function handleToggleIndemnity(sportId: string, current: boolean) {
    if (!id) return;
    setEnrollmentError(null);
    try {
      const res = await api.updatePlayerEnrollment(id, sportId, { indemnity_complete: !current });
      setEnrollments((prev) =>
        prev.map((e) => (e.sport_id === sportId ? res.enrollment : e)),
      );
    } catch (err) {
      setEnrollmentError(
        err instanceof api.ApiError ? err.message : "Failed to update enrollment",
      );
    }
  }

  // 155 -- attach (or clear) the completed indemnity form. Attaching marks the
  // enrollment indemnity complete; clearing marks it incomplete (HARD block).
  async function handleSetIndemnityDoc(sportId: string, current: string | null) {
    if (!id) return;
    setEnrollmentError(null);
    const url = window.prompt(
      "Paste the URL/reference of the completed indemnity form (leave blank to clear):",
      current ?? "",
    );
    if (url === null) return; // cancelled
    try {
      const res = await api.updatePlayerEnrollment(id, sportId, {
        indemnity_document_url: url.trim() || null,
      });
      setEnrollments((prev) =>
        prev.map((e) => (e.sport_id === sportId ? res.enrollment : e)),
      );
    } catch (err) {
      setEnrollmentError(err instanceof api.ApiError ? err.message : "Failed to update indemnity");
    }
  }

  async function handleDownloadBlankIndemnity() {
    try {
      const { url } = await api.getIndemnityBlankForm();
      window.open(url, "_blank", "noopener");
    } catch {
      setEnrollmentError("Failed to fetch the blank indemnity form");
    }
  }

  // 149 -- set/clear the seasonal club affiliation on an enrollment
  async function handleSetClub(sportId: string, clubId: string) {
    if (!id) return;
    setEnrollmentError(null);
    try {
      const res = await api.updatePlayerEnrollment(id, sportId, { club_id: clubId || null });
      setEnrollments((prev) =>
        prev.map((e) => (e.sport_id === sportId ? res.enrollment : e)),
      );
    } catch (err) {
      setEnrollmentError(
        err instanceof api.ApiError ? err.message : "Failed to update club",
      );
    }
  }

  async function handleToggleOverride(sportId: string, ageGroupId: string, enrolled: boolean) {
    if (!id) return;
    setEnrollmentError(null);
    const enrollment = enrollments.find((e) => e.sport_id === sportId);
    if (!enrollment) return;

    const newOverrides = enrolled
      ? enrollment.also_eligible_for.filter((x) => x !== ageGroupId)
      : [...enrollment.also_eligible_for, ageGroupId];

    try {
      const res = await api.updatePlayerEnrollment(id, sportId, { also_eligible_for: newOverrides });
      setEnrollments((prev) =>
        prev.map((e) => (e.sport_id === sportId ? res.enrollment : e)),
      );
    } catch (err) {
      setEnrollmentError(
        err instanceof api.ApiError ? err.message : "Failed to update overrides",
      );
    }
  }

  // 158 -- admin age-group override (play up / down). DOB stays canonical; this
  // records an explicit exception and triggers the indemnity requirement (155).
  async function handleSetAgeOverride(sportId: string, ageGroupId: string) {
    if (!id) return;
    setEnrollmentError(null);
    let reason: string | null = null;
    if (ageGroupId) {
      reason = window.prompt("Reason for the age-group override (recorded exception):", "") ?? "";
    }
    try {
      const res = await api.updatePlayerEnrollment(id, sportId, {
        age_group_override_id: ageGroupId || null,
        age_group_override_reason: ageGroupId ? (reason || null) : null,
      });
      setEnrollments((prev) =>
        prev.map((e) => (e.sport_id === sportId ? res.enrollment : e)),
      );
    } catch (err) {
      setEnrollmentError(
        err instanceof api.ApiError ? err.message : "Failed to update age-group override",
      );
    }
  }

  if (loading) {
    return <p className="text-muted-foreground" data-page="player-edit" data-state="loading">Loading...</p>;
  }

  if (loadError) {
    return (
      <div data-page="player-edit" data-state="error">
        <LoadErrorCard error={loadError} entityLabel="player" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-page="player-edit" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Edit Player</h1>
        <p className="text-muted-foreground">Update player details</p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Player Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-region="form" data-form="player-edit">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            <div className="flex flex-col gap-2">
              <Label htmlFor="first-name">First Name</Label>
              <Input
                id="first-name"
                data-field="first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="surname">Surname</Label>
              <Input
                id="surname"
                data-field="surname"
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                At least one of first name or surname is required.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="dob">Date of Birth</Label>
              <Input
                id="dob"
                data-field="date-of-birth"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="nickname">Nickname</Label>
              <Input
                id="nickname"
                data-field="nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                maxLength={100}
              />
              <p className="text-xs text-muted-foreground">
                What the player is commonly called.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="grade">Grade</Label>
              <select
                id="grade"
                data-field="grade"
                title="Grade"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">Not set</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((g) => (
                  <option key={g} value={g}>
                    Grade {g}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Administrative metadata -- not used for age group eligibility.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="player-code">Player Code</Label>
              <Input
                id="player-code"
                data-field="player-code"
                placeholder="e.g. SAMS-12345"
                value={playerCode}
                onChange={(e) => setPlayerCode(e.target.value)}
                maxLength={100}
              />
              <p className="text-xs text-muted-foreground">
                External school system identifier (e.g. SAMS ID).
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="family-code">Family Code</Label>
              <Input
                id="family-code"
                data-field="family-code"
                value={familyCode}
                onChange={(e) => setFamilyCode(e.target.value)}
                maxLength={50}
              />
              <p className="text-xs text-muted-foreground">
                Family code from the school admin system. Siblings share the same code.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <input
                id="active"
                data-field="active"
                type="checkbox"
                title="Active player"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              <Label htmlFor="active">Active player</Label>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting}>
                {submitting ? "Saving..." : "Save Changes"}
              </Button>
              <Link
                to="/players"
                className={buttonVariants({ variant: "outline" })}
                data-action="cancel"
              >
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Sport Enrollments -- managed separately via enrollment endpoints */}
      <Card className="max-w-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Sport Enrollments</CardTitle>
            {availableSports.length > 0 && (
              <select
                title="Add sport enrollment"
                className="flex h-8 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value=""
                onChange={(e) => {
                  if (e.target.value) handleAddEnrollment(e.target.value);
                }}
              >
                <option value="">+ Add sport...</option>
                {availableSports.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {enrollmentError && (
            <p className="mb-3 text-sm text-destructive">{enrollmentError}</p>
          )}

          {enrollments.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Not enrolled in any sports. Use the dropdown above to add a sport.
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {enrollments.map((enrollment) => {
                const sportAgeGroups = allAgeGroups.filter(
                  (ag) => ag.sport_id === enrollment.sport_id,
                );

                return (
                  <div
                    key={enrollment.sport_id}
                    className="rounded-md border border-border p-3"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{enrollment.sport_name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveEnrollment(enrollment.sport_id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>

                    {/* Indemnity (155) -- incomplete = red, HARD-blocks selection */}
                    <div className="mt-2 flex flex-col gap-2" data-field="indemnity">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          title="Indemnity complete"
                          checked={enrollment.indemnity_complete}
                          onChange={() =>
                            handleToggleIndemnity(
                              enrollment.sport_id,
                              enrollment.indemnity_complete,
                            )
                          }
                          className="h-4 w-4 rounded border-input"
                        />
                        <span className="text-sm">Indemnity complete</span>
                        {!enrollment.indemnity_complete && (
                          <span className="inline-flex items-center rounded-full bg-[#FCEBEB] px-2 py-0.5 text-[11px] font-medium text-[#791F1F]">
                            Incomplete - blocks selection
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs">
                        <button
                          type="button"
                          onClick={handleDownloadBlankIndemnity}
                          className="text-primary hover:underline"
                        >
                          Download blank form
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSetIndemnityDoc(enrollment.sport_id, enrollment.indemnity_document_url)}
                          className="text-primary hover:underline"
                        >
                          {enrollment.indemnity_document_url ? "Replace completed form" : "Attach completed form"}
                        </button>
                        {enrollment.indemnity_document_url && (
                          <a
                            href={enrollment.indemnity_document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-muted-foreground hover:underline"
                          >
                            View on file
                          </a>
                        )}
                      </div>
                    </div>

                    {/* 149 -- club affiliation for the club season */}
                    <div className="mt-3 flex flex-col gap-1.5">
                      <span className="text-xs font-medium">Club (for the club season)</span>
                      <select
                        title="Club affiliation"
                        data-field="club"
                        value={enrollment.club_id ?? ""}
                        onChange={(e) => handleSetClub(enrollment.sport_id, e.target.value)}
                        className="flex h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <option value="">No club (school only)</option>
                        {clubs.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* 158 -- admin age-group override (play up / down). An
                        explicit recorded exception; DOB stays canonical. The
                        permitted-to-play colour is derived by the API. */}
                    <div className="mt-3 flex flex-col gap-1.5" data-field="age-override">
                      <span className="text-xs font-medium">Age-group override (play up / down)</span>
                      <select
                        title="Age-group override"
                        data-field="age-override-select"
                        value={enrollment.age_group_override_id ?? ""}
                        onChange={(e) => handleSetAgeOverride(enrollment.sport_id, e.target.value)}
                        className="flex h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <option value="">No override (use DOB default)</option>
                        {sportAgeGroups.map((ag) => (
                          <option key={ag.id} value={ag.id}>
                            {ag.name}
                          </option>
                        ))}
                      </select>
                      {enrollment.age_group_override_id && enrollment.override_direction !== "default" && (
                        <div className="flex flex-wrap items-center gap-2 text-xs" data-field="override-status">
                          <span className="inline-flex items-center rounded-full bg-[#E6F1FB] px-2 py-0.5 font-medium text-[#0C447C]">
                            {OVERRIDE_DIRECTION_LABEL[enrollment.override_direction]}
                            {enrollment.age_group_override_name ? ` to ${enrollment.age_group_override_name}` : ""}
                          </span>
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 font-medium ${OVERRIDE_PERMISSION_BADGE[enrollment.override_permission].cls}`}
                            data-permission={enrollment.override_permission}
                          >
                            {OVERRIDE_PERMISSION_BADGE[enrollment.override_permission].label}
                          </span>
                          {enrollment.age_group_override_reason && (
                            <span className="text-muted-foreground">{enrollment.age_group_override_reason}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* 079 -- Play-up overrides: only groups that allow_play_up
                        AND that are within +1/+2 of the player's natural group */}
                    {(() => {
                      const birthYear = dateOfBirth
                        ? parseInt(dateOfBirth.substring(0, 4), 10)
                        : NaN;
                      const currentYear = new Date().getFullYear();
                      const offset = Number.isFinite(birthYear)
                        ? currentYear - birthYear
                        : null;
                      const naturalGroup =
                        offset === null
                          ? null
                          : sportAgeGroups.find(
                              (g) =>
                                offset >= g.year_offset_min &&
                                offset <= g.year_offset_max,
                            );
                      const validTargets = sportAgeGroups.filter((ag) => {
                        if (!ag.allows_play_up) return false;
                        if (!naturalGroup) return false;
                        if (ag.id === naturalGroup.id) return false;
                        const gap = ag.year_offset_min - naturalGroup.year_offset_min;
                        return gap >= 1 && gap <= 2;
                      });
                      if (!naturalGroup) {
                        return (
                          <div className="mt-3">
                            <p className="text-xs text-muted-foreground">
                              Play-up overrides unavailable -- no natural age group resolved for this DOB + sport.
                            </p>
                          </div>
                        );
                      }
                      if (validTargets.length === 0) {
                        return (
                          <div className="mt-3">
                            <p className="text-xs text-muted-foreground">
                              Play-up overrides unavailable for this age band. Primary-ladder groups don&apos;t allow play-up; high-school groups accept overrides up to +2 from the player&apos;s natural group.
                            </p>
                          </div>
                        );
                      }
                      return (
                        <div className="mt-3">
                          <p className="mb-1.5 text-xs font-medium">
                            Play-up overrides
                          </p>
                          <p className="mb-2 text-xs text-muted-foreground">
                            Nominate a higher age group this player may also play in. SARU +2 rule: targets capped at two bands above {naturalGroup.name}.
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {validTargets.map((ag) => {
                              const isOverride =
                                enrollment.also_eligible_for.includes(ag.id);
                              return (
                                <button
                                  key={ag.id}
                                  type="button"
                                  onClick={() =>
                                    handleToggleOverride(
                                      enrollment.sport_id,
                                      ag.id,
                                      isOverride,
                                    )
                                  }
                                  className={`inline-flex cursor-pointer items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                                    isOverride
                                      ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
                                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                                  }`}
                                >
                                  {ag.name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {id && <PlayerHealthSection playerId={id} />}
      {id && <PlayerInjurySection playerId={id} />}

      {id && <CoachNotesSection playerId={id} />}
    </div>
  );
}
